export interface ImageValidationResult {
  isValid: boolean;
  error?: string;
}

export interface ImageConstraints {
  maxSizeKb: number;
  maxWidth: number;
  maxHeight: number;
}

export const LOGO_IMAGE_CONSTRAINTS: ImageConstraints = {
  maxSizeKb: 50,
  maxWidth: 350,
  maxHeight: 350,
};

export const NEWS_IMAGE_CONSTRAINTS: ImageConstraints = {
  maxSizeKb: 250,
  maxWidth: 1024,
  maxHeight: 1024,
};

export const validateImage = (
  file: File,
  constraints: ImageConstraints = LOGO_IMAGE_CONSTRAINTS
): ImageValidationResult => {
  // Check file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Only JPG and PNG files are allowed.'
    };
  }

  const maxSize = constraints.maxSizeKb * 1024;
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: `File size must be less than ${constraints.maxSizeKb}KB.`
    };
  }

  return { isValid: true };
};

export const validateImageDimensions = (
  file: File,
  constraints: ImageConstraints = LOGO_IMAGE_CONSTRAINTS
): Promise<ImageValidationResult> => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      
      if (img.width > constraints.maxWidth || img.height > constraints.maxHeight) {
        resolve({
          isValid: false,
          error: `Image dimensions must not exceed ${constraints.maxWidth}x${constraints.maxHeight} pixels.`
        });
      } else {
        resolve({ isValid: true });
      }
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({
        isValid: false,
        error: 'Failed to load image for validation.'
      });
    };
    
    img.src = url;
  });
};

export const resizeImage = (
  file: File,
  maxWidth: number = LOGO_IMAGE_CONSTRAINTS.maxWidth,
  maxHeight: number = LOGO_IMAGE_CONSTRAINTS.maxHeight
): Promise<File> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;
      
      // Draw and resize image
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to blob
      canvas.toBlob((blob) => {
        if (blob) {
          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        } else {
          resolve(file); // Fallback to original file
        }
      }, file.type, 0.8); // 0.8 quality for JPEG compression
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // Fallback to original file
    };
    
    img.src = url;
  });
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}; 